import { SyntheticEvent, useCallback } from 'react';
import { useForm } from 'react-hook-form';

import * as releaseApi from 'api/releaseApi';
import { REGISTRATION_STEP } from 'constants/registrationStep';
import { useToast } from 'hooks/useToast';
import { useCustomerStore } from 'page/ReleasePage/TaskPage/hooks/useCustomersStore';
import { useAppDispatch, useAppSelector } from 'store/hooks/useStore';
import { loaderActions } from 'store/reducers/loaderReducer/loaderSlice';
import { customersSelectors } from 'store/reducers/releaseReducers/releaseTaskReducer/customersSlice';
import {
  registrationActions,
  registrationSelectors,
} from 'store/reducers/releaseReducers/releaseTaskReducer/registrationSlice';
import { ReleaseEnvironment } from 'types/releaseEnvironment';
import {
  Condition,
  CONDITIONS,
  ReleaseTask,
  ReleaseTaskFormField,
} from 'types/releaseTask';
import { EditHookReturnType } from '../types';
import { useListStore } from './useListStore';

/**
 * 当日日付かつ`00:00`を初期値にするための処理
 *
 * ```ts
 * // 日本時間で`2023-01-16 12:34`に開いた場合
 * const startsAt = getStartsAtDefaultValue();
 * console.log(startsAt.toLocaleString());
 * // > タスク開始日には"2023/1/16 0:00:00"を設定する
 * ```
 */
const getStartsAtDefaultValue = () => {
  const now = new Date();

  return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
};

/**
 * 当日日付かつ`00:00`を初期値にするための処理
 *
 * ```ts
 * // 日本時間で`2023-01-16 12:34`に開いた場合
 * const endsAt = getEndsAtDefaultValue();
 * console.log(endsAt.toLocaleString());
 * // > タスク終了日には"2023/1/16 1:00:00"を設定する
 * ```
 */
const getEndsAtDefaultValue = () => {
  const now = new Date();

  return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 1, 0, 0);
};

/**
 * タスク登録 store の実装をタスクから隠すための中間層
 */
export const useRegistrationStore = (): EditHookReturnType => {
  const { fetchCustomers, uncheckAllCustomers } = useCustomerStore();
  const dispatch = useAppDispatch();
  const { fetchReleaseTasks } = useListStore();
  const toast = useToast();
  const form = useForm<ReleaseTaskFormField>({
    mode: 'all',
    defaultValues: {
      startsAt: getStartsAtDefaultValue(),
      endsAt: getEndsAtDefaultValue(),
      condition: CONDITIONS.ALL,
      version: undefined,
    },
  });
  const { getValues, reset, handleSubmit, control } = form;
  const currentModalStep = useAppSelector(
    registrationSelectors.currentModalStep,
  );

  const CreateReleaseEnvironmentList = (
    list: ReleaseEnvironment,
  ): { id: string; name: string } => {
    const Environment = {
      id: list.manifestVersion ? list.manifestVersion : '',
      name: list.name,
    };

    return Environment;
  };

  const releaseEnvironments = useAppSelector(
    registrationSelectors.releaseEnvironments,
  ).map((releaseEnvironment) =>
    CreateReleaseEnvironmentList(releaseEnvironment),
  );

  const createTargetGroupList = (
    item: string,
  ): { id: string; name: string } => {
    const TargetGroupList = {
      id: item,
      name: item,
    };

    return TargetGroupList;
  };

  const targetGroupList = useAppSelector(
    registrationSelectors.targetGroupList,
  ).map((item) => createTargetGroupList(item));

  const isOpenModal = useAppSelector(registrationSelectors.isOpenModal);

  const onOpenModal = useCallback(() => {
    dispatch(registrationActions.modalOpened());
    void fetchCustomers();
    // 画面を開いた時の初期値セット
    dispatch(
      registrationActions.conditionUpdated({ condition: CONDITIONS.ALL }),
    );
    dispatch(
      registrationActions.updateTargetGroup({
        targetGroup: targetGroupList[0].name,
      }),
    );
  }, [dispatch, fetchCustomers, targetGroupList]);

  const onCloseModal = () => {
    dispatch(registrationActions.initialize());
    reset();
  };

  const onCloseModalWhenCompleted = () => {
    onCloseModal();
    void fetchReleaseTasks();
  };

  const onConfirm = () => {
    dispatch(registrationActions.updateStep(REGISTRATION_STEP.CONFIRMATION));
  };

  const onGoBack = () => {
    dispatch(registrationActions.updateStep(REGISTRATION_STEP.INPUT));
  };

  const selectedCustomers = useAppSelector(
    customersSelectors.selectedCustomers,
  );

  const onSubmit = handleSubmit(
    async (values: ReleaseTaskFormField) => {
      dispatch(loaderActions.setLoading(true));
      const params: ReleaseTask = {
        taskName: values.taskName,
        startsAt: new Date(values.startsAt).toISOString(),
        endsAt: new Date(values.endsAt).toISOString(),
        group: values.group,
        condition: values.condition,
        version: values.version,
        targets:
          values.condition === CONDITIONS.ALL
            ? null
            : selectedCustomers.map((selectCustemer) => selectCustemer.id),
      };
      const result = await releaseApi.addTask(params);
      if (result.ok) {
        dispatch(registrationActions.updateStep(REGISTRATION_STEP.RESULT));
      } else {
        toast({
          status: 'error',
          description: `タスクの登録に失敗しました: ${result.err.message}`,
        });
      }
      dispatch(loaderActions.setLoading(false));
    },
    () => {
      toast({
        status: 'error',
        description: 'タスクの登録に失敗しました。',
      });
    },
  );

  const handleChangeCondition = useCallback(
    (_condition: Condition) => {
      if (_condition === CONDITIONS.ALL) {
        // `全顧客`を選択すると顧客は画面上に表示されない。
        // そのため、ユーザーが「選択されたまま」であることを認知することが難しいと考え、
        // 切り替えたタイミングで選択していた顧客をクリアする。
        uncheckAllCustomers();
      }
      dispatch(
        registrationActions.conditionUpdated({
          condition: _condition,
        }),
      );
    },
    [dispatch, uncheckAllCustomers],
  );

  /**
   * ターゲットグループを選択した時の処理です。
   *
   * `useEditStore`と同じ処理だが、
   * 新規・変更で処理が変更される可能性はあり得るため、統合は諦めて分割する。
   */
  const handleChangeSelectTargetGroupInput = useCallback(
    (e: SyntheticEvent<HTMLSelectElement>) => {
      // 値を切り替えると選んでいた顧客は画面上に表示されない。
      // そのため、ユーザーが「選択されたまま」であることを認知することが難しいと考え、
      // 切り替えたタイミングで選択していた顧客をクリアする。
      uncheckAllCustomers();
      const { value } = e.currentTarget;
      form.setValue('group', e.currentTarget.value);
      dispatch(registrationActions.updateTargetGroup({ targetGroup: value }));
    },
    [dispatch, form, uncheckAllCustomers],
  );

  const shouldShowCustomerList = useAppSelector(
    registrationSelectors.shouldShowCustomerList,
  );

  return {
    mode: '登録',
    isOpenModal,
    currentModalStep,
    form,
    control,
    releaseEnvironments,
    targetGroupList,
    shouldShowCustomerList,
    selectedCustomers,
    values: getValues(),
    onOpenModal,
    onCloseModal,
    onCloseModalWhenCompleted,
    onConfirm,
    onGoBack,
    onSubmit,
    onOpenModalDelete: () => null,
    handleChangeCondition,
    handleChangeSelectTargetGroupInput,
  };
};
